import {
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  collection,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from "../lib/firebase";
import type {
  SurveyType,
  GetSurveyResultsInput,
  GetSurveyResultsResponse,
  Session,
  BulkCreateSurveysInput,
  BulkCreateSurveysResponse,
  BulkResolveSurveysInput,
  BulkResolveSurveysResponse,
} from "@event-planner/shared";
import { dateTimeLocalToTimestamp } from "../utils/timestamps";

const getSurveyResultsCallable = httpsCallable<
  GetSurveyResultsInput,
  GetSurveyResultsResponse
>(functions, "getSurveyResults");

const bulkCreateSurveysCallable = httpsCallable<
  BulkCreateSurveysInput,
  BulkCreateSurveysResponse
>(functions, "bulkCreateSurveys");

const bulkResolveSurveysCallable = httpsCallable<
  BulkResolveSurveysInput,
  BulkResolveSurveysResponse
>(functions, "bulkResolveSurveys");

export async function bulkCreateSurveys(
  input: BulkCreateSurveysInput,
): Promise<BulkCreateSurveysResponse> {
  const result = await bulkCreateSurveysCallable(input);
  return result.data;
}

export async function bulkResolveSurveys(
  input: BulkResolveSurveysInput,
): Promise<BulkResolveSurveysResponse> {
  const result = await bulkResolveSurveysCallable(input);
  return result.data;
}

/**
 * Fetch server-computed aggregate results for a survey. Never reads
 * `surveyResponses` directly — security rules correctly deny organizers
 * access to individual response docs (ADR-054 §3, anonymity guarantee).
 * The callable reads via Admin SDK and returns per-question aggregates.
 */
export async function getSurveyResults(
  surveyId: string,
): Promise<GetSurveyResultsResponse> {
  const result = await getSurveyResultsCallable({ surveyId });
  return result.data;
}

export interface QuestionCreateInput {
  /** Stable id generated client-side; persisted onto the doc. */
  id: string;
  prompt: string;
  type: SurveyType;
  options: string[];
  // Per-question presentation metadata (Power BI dashboard labels)
  ratingAxisLabel?: string;
  ratingScaleMin?: string;
  ratingScaleMax?: string;
  optionAxisLabel?: string;
  responseLabel?: string;
  chartSubtitle?: string;
}

export interface CreateSurveyInput {
  eventId: string;
  sessionId: string | null;
  /** Ordered list of questions. At least one. */
  questions: QuestionCreateInput[];
  /** "now" opens immediately; "later" sets status to "scheduled". */
  scheduleMode: "now" | "later";
  /** datetime-local string in event timezone. Required when scheduleMode === "later". */
  scheduledAt: string | null;
  /** Event timezone for converting scheduledAt to UTC. */
  timezone: string;
  /** If true, creates an accompanying push notification. */
  sendNotification: boolean;
  notificationTitle: string;
  notificationBody: string;
}

/**
 * Creates a survey and optionally an accompanying push notification.
 *
 * When `sendNotification` is true, the notification is created in the same
 * `notifications` collection as organizer-composed notifications, so it
 * appears in the Notifications list. Its deep link points to the survey.
 *
 * Timing:
 * - scheduleMode "now": survey status = "open", notification status = "sent"
 *   (triggers sendNotification CF immediately)
 * - scheduleMode "later": survey status = "scheduled", notification status = "scheduled"
 *   (processScheduledNotifications fires the push at scheduledAt;
 *    processScheduledSurveys opens the survey at the same time)
 */
export async function createSurvey(
  input: CreateSurveyInput,
): Promise<{ surveyId: string; notificationId: string | null }> {
  const user = auth.currentUser;
  if (!user) throw new Error("Must be signed in to create a survey");

  const isNow = input.scheduleMode === "now";
  const surveyStatus = isNow ? "open" : "scheduled";

  let scheduledAt: Timestamp | null = null;
  if (!isNow && input.scheduledAt) {
    scheduledAt = dateTimeLocalToTimestamp(input.scheduledAt, input.timezone);
  }

  // 1. Create the survey
  const questions = input.questions.map((q) => ({
    id: q.id,
    prompt: q.prompt,
    type: q.type,
    options: q.type === "single_choice" ? q.options : [],
    ratingAxisLabel: q.ratingAxisLabel || null,
    ratingScaleMin: q.ratingScaleMin || null,
    ratingScaleMax: q.ratingScaleMax || null,
    optionAxisLabel: q.optionAxisLabel || null,
    responseLabel: q.responseLabel || null,
    chartSubtitle: q.chartSubtitle || null,
  }));
  const surveyRef = await addDoc(collection(db, "surveys"), {
    eventId: input.eventId,
    sessionId: input.sessionId,
    createdByUserId: user.uid,
    questions,
    anonymous: true,
    status: surveyStatus,
    openAt: isNow ? serverTimestamp() : scheduledAt,
    closeAt: null,
    notificationId: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // 2. Optionally create the push notification
  let notificationId: string | null = null;
  if (input.sendNotification) {
    const notifStatus = isNow ? "sent" : "scheduled";
    const deepLink = `com.serenica.eventplanner://events/${input.eventId}/surveys/${surveyRef.id}`;

    // Targeting (ADR-120): session-scoped surveys reuse that session's
    // audienceId; event-scoped surveys target the deterministic All
    // Members system audience.
    let audienceId = `${input.eventId}_all-members`;
    if (input.sessionId) {
      const sessionSnap = await getDoc(doc(db, "sessions", input.sessionId));
      const session = sessionSnap.exists()
        ? ({ id: sessionSnap.id, ...sessionSnap.data() } as Session)
        : null;
      if (session?.audienceId) audienceId = session.audienceId;
    }

    const notifRef = await addDoc(collection(db, "notifications"), {
      eventId: input.eventId,
      createdByUserId: user.uid,
      channels: ["push"],
      title: input.notificationTitle,
      body: input.notificationBody,
      deepLink,
      emailSubject: null,
      emailBodyHtml: null,
      audienceId,
      targetedPersonIds: [],
      status: notifStatus,
      scheduledAt,
      sentAt: null,
      recipientCount: null,
      pushTokenCount: null,
      pushSuccessCount: null,
      pushFailureCount: null,
      triggerType: "manual",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    notificationId = notifRef.id;

    // Link the notification back to the survey
    await updateDoc(surveyRef, { notificationId });
  }

  return { surveyId: surveyRef.id, notificationId };
}

export async function closeSurvey(surveyId: string): Promise<void> {
  await updateDoc(doc(db, "surveys", surveyId), {
    status: "closed",
    closeAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function reopenSurvey(surveyId: string): Promise<void> {
  await updateDoc(doc(db, "surveys", surveyId), {
    status: "open",
    closeAt: null,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteSurvey(surveyId: string): Promise<void> {
  await deleteDoc(doc(db, "surveys", surveyId));
}
